import { Link } from 'react-router-dom';
import { ArrowRight, EnvelopeSimple, HandHeart, MapTrifold, ShieldCheck } from '@phosphor-icons/react';
import { useI18n } from '@/lib/i18n';

const exploreLinks = [
  { href: '/#find-help', labelKey: 'shell.findByPlace' },
  { href: '/#state-directory', labelKey: 'shell.townCenters' },
  { href: '/food', labelKey: 'nav.food' },
  { href: '/community', labelKey: 'home.community' },
] as const;

const trustLinks = [
  { href: '/about', labelKey: 'footer.about' },
  { href: '/county-verification', labelKey: 'home.coverage' },
  { href: '/connecting-hands', labelKey: 'footer.getInvolved' },
  { href: '/contact', labelKey: 'nav.contact' },
] as const;

export const Footer = () => {
  const { t } = useI18n();

  return (
    <footer
      aria-label={t('footer.siteLabel')}
      className="relative mx-auto max-w-[1380px] px-3 pb-5 sm:px-5 lg:px-7 lg:pb-8"
    >
      <div className="overflow-hidden rounded-[1.6rem] border border-[#173f31]/10 bg-[#fffaf0]/82 shadow-[0_18px_46px_rgba(38,50,40,0.08)] backdrop-blur-xl">
        <div className="grid gap-6 p-5 sm:grid-cols-2 sm:p-6 xl:grid-cols-[1.35fr_0.75fr_0.75fr_1fr]">
          <section>
            <Link to="/" className="inline-flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-2xl bg-[#173f31] text-[#fff8e7] shadow-[0_12px_24px_rgba(23,63,49,0.18)]">
                <HandHeart size={22} weight="fill" />
              </span>
              <span>
                <span className="block text-[9px] font-black uppercase tracking-[0.2em] text-[#965039]">
                  {t('shell.publicNetwork')}
                </span>
                <span className="block font-serif text-xl font-black tracking-[-0.04em] text-[#17231d]">
                  {t('nav.brand')}
                </span>
              </span>
            </Link>
            <p className="mt-3 max-w-md text-sm leading-6 text-[#647068]">{t('footer.description')}</p>
            <div className="mt-4 inline-flex items-start gap-2 rounded-xl border border-[#173f31]/10 bg-[#173f31]/[0.04] px-3 py-2">
              <ShieldCheck size={17} weight="fill" className="mt-0.5 shrink-0 text-[#397257]" />
              <p className="max-w-md text-xs leading-5 text-[#59675e]">{t('shell.sourceFirstBody')}</p>
            </div>
          </section>

          <nav aria-label={t('footer.quickLinks')}>
            <h2 className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.16em] text-[#59675e]">
              <MapTrifold size={17} weight="duotone" className="text-[#397257]" />
              {t('footer.quickLinks')}
            </h2>
            <ul className="mt-3 space-y-2">
              {exploreLinks.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="inline-flex min-h-7 items-center gap-1 text-sm font-bold text-[#314139] transition hover:text-[#995038]"
                  >
                    {t(item.labelKey)}
                    <ArrowRight size={12} weight="bold" />
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label={t('shell.trustTitle')}>
            <h2 className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.16em] text-[#59675e]">
              <ShieldCheck size={17} weight="duotone" className="text-[#397257]" />
              {t('shell.trustTitle')}
            </h2>
            <ul className="mt-3 space-y-2">
              {trustLinks.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="inline-flex min-h-7 items-center gap-1 text-sm font-bold text-[#314139] transition hover:text-[#995038]"
                  >
                    {t(item.labelKey)}
                    <ArrowRight size={12} weight="bold" />
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <section>
            <h2 className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.16em] text-[#59675e]">
              <EnvelopeSimple size={17} weight="duotone" className="text-[#397257]" />
              {t('nav.contact')}
            </h2>
            <p className="mt-3 text-sm leading-6 text-[#647068]">{t('footer.emailNote')}</p>
            <a
              href="mailto:hello@planetearth.help"
              className="mt-3 inline-flex min-h-10 items-center gap-2 rounded-xl bg-[#173f31] px-4 text-sm font-black text-[#fff8e7] transition hover:bg-[#225742]"
            >
              hello@planetearth.help
              <ArrowRight size={14} weight="bold" />
            </a>
          </section>
        </div>

        <div className="flex flex-col items-center justify-between gap-3 border-t border-[#173f31]/10 px-5 py-4 text-center text-[11px] font-semibold text-[#667168] sm:flex-row sm:px-6 sm:text-left">
          <p>
            &copy; {new Date().getFullYear()} {t('nav.brand')}. {t('footer.rights')}
          </p>
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2">
            <Link to="/privacy" className="hover:text-[#995038]">
              {t('footer.privacy')}
            </Link>
            <Link to="/terms" className="hover:text-[#995038]">
              {t('footer.terms')}
            </Link>
            <Link to="/accessibility" className="hover:text-[#995038]">
              {t('footer.accessibility')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
